import { MotilityAnalysis } from "./motility";
import {
  EyeDisplacement,
  PositionWithTimeStamp,
  HeadVelocity,
  PupilSizeData,
  GazeDirectionVector,
  GazeWithDelta,
  FDTBehaviourType,
  DistanceData,
  EyeImageData,
  EyeType,
  GazeData,
  GoldmannSize,
  Direction,
  PercentageWithTimeStamp,
  PercentagePoint,
  Position,
  SnellenSize,
  TestCategory,
  TestLanguage,
  TestType,
  TextOrientation,
} from "./core";
import { BaseTestResult } from "./results";

export interface FDTTestCustomConfiguration {
  constants: FDTContrasts;
  behaviourType?: FDTBehaviourType;
}

export interface FPFNInfo {
  fp: {
    val: number;
    total: number;
  };
  fn: {
    val: number;
    total: number;
  };
}

export interface VFATestSnapshot {
  index?: number;
  value?: number;
  blindSpotSeen?: number;
  blindSpotTotal?: number;
  fpFnInfo?: FPFNInfo;
  fixation?: string;
  fovealThreshold?: number;
}

export interface VFACollectedData {
  pointValues: number[];
  fixation: string;
  warning: number;
  seenFL: number;
  fpFnInfo?: FPFNInfo;
  fovealThreshold?: number;
  blindSpotSeen?: number;
  blindSpotTotal?: number;
}

export interface VFATestCustomConfiguration {
  blindSpotTesting: boolean;
  testBehaviour: VFABehaviour;
  blindSpotMonitoringEnabled: boolean;
  fovealThresholdTestingEnabled: boolean;
  enablePausingTheTestWithButton: boolean;
  showTutorial: boolean;
  fixationSize: GoldmannSize;
  stimuliSize: GoldmannSize;
}

export interface KineticTestCustomConfiguration {
  stimuliSize: GoldmannSize;
  fixationSize: GoldmannSize;
  testPattern: KineticPerimetryPattern;
  dbColor: ThresholdLevel | number;
  speed: number;
  fixationShiftAngle: number;
  testTypeCircleAngle: number;
  numberOfStimuliStarts: number;
}

export enum CPTConfigMode {
  Default = "default",
  Demo = "demo",
  Adult = "adult",
  AdultDemo = "adultDemo",
}

export enum DyscalculiaConfigMode {
  Adult = "adult",
  Child = "child",
  AdultAdaptive = "adultAdaptive",
  AdultAdaptiveFast = "adultAdaptiveFast",
}

export enum ThresholdLevel {
  thresholdLevel5 = 5,
  thresholdLevel10 = 10,
  thresholdLevel15 = 15,
  thresholdLevel20 = 20,
}

export enum VFABehaviour {
  FAST_4_2 = 0,
  FAST_4 = 1,
  FULL_THRESH_4 = 2,
  SITA_LIKE = 3,
  SIMPLE_TEST = 4,
  TOP = 5,
  SWAP_TOP = 6,
  TWO_ZONE = 7,
  THREE_ZONE = 8,
  ADAPTIVE_TRACKING = 9,
}

export enum OptionalVFATests {
  SWAP = "SWAP",
  FDT = "FDT",
  KINETIC = "KINETIC",
  KIN = "KIN",
}

export enum EyeCoverAction {
  RightKeptOpenLeftKeptOpen = "RightKeptOpenLeftKeptOpen",
  CoverRightEyeLeftKeptOpen = "CoverRightEyeLeftKeptOpen",
  OpenRightEyeLeftKeptOpen = "OpenRightEyeLeftKeptOpen",
  CoverLeftEyeRightKeptOpen = "CoverLeftEyeRightKeptOpen",
  OpenLeftEyeRightKeptOpen = "OpenLeftEyeRightKeptOpen",
}

export interface FDTContrasts {
  initialContrast: number;
  secondContrast: number;
  thirdContrast: number;
}
//-------- FDT Specific Data

export interface FDTData {
  fdtDatas: FDTSnapshot[];
  firstButtonPressed: boolean;
}
export interface FDTSnapshot {
  label: FDTLabel;
  contrast: number;
  value?: number;
  index?: number;
}

export interface Fovea extends FDTSnapshot {
  value: number;
  db: number;
}

export enum FDTLabel {
  P_GTE_1 = "P_GTE_1",
  P_LT_1 = "P_LT_1",
  P_LT_0_5 = "P_LT_0_5",
  P_GTE_5 = "P_GTE_5",
  P_LT_5 = "P_LT_5",
  P_LT_2 = "P_LT_2",
  MAX = "MAX",
}
export interface FDTTestResult extends BaseTestResult {
  category: TestCategory.FDT;
  testType: TestType.C_20_1 | TestType.C_20_5 | TestType.N_30_1 | TestType.N_30_5;
  fdtPoints: FDTSnapshot[];
  fovea?: Fovea;
  displacementList: EyeDisplacement[];
}
//-------- Contrast Sensitivity Specific Data
export interface ContrastSensitivityData {
  grating?: number;
  contrast?: number;
  fpFnInfo?: FPFNInfo;
}

export interface ContrastSensitivityOrPelliRobsonData {
  contrastSensitivityDatas?: ContrastSensitivityData[];
  pelliRobsonData?: PelliRobsonData;
  fpFnInfo?: FPFNInfo;
}

export interface PelliRobsonData {
  pelliRobsonLetterSet: ScoredLetterSet[];
  pelliRobsonIndex: number;
}
export interface ContrastSensitivityTestResult extends BaseTestResult {
  category: TestCategory.ContrastSensitivity;
  results: ContrastSensitivityAndPelliRobsonResult;
  fpFnInfo?: FPFNInfo;
}

export interface ContrastSensitivityAndPelliRobsonResult {
  contrastResults: ContrastSensitivityData[];
  pelliRobsonResults: PelliRobsonResult;
  fpFnInfo?: FPFNInfo;
}

export interface PelliRobsonResult {
  lineResults: LineResult[];
}

export interface LineResult {
  index: number;
  letters: LetterResult[];
}

export interface LetterResult {
  letter: string;
  isTrue: boolean;
  scale: number;
}
//-------- Pupillometry Specific Data
export interface PupillometryCustomConfiguration {
  lapCount: number;
  initialWaitDuration: number;
  stimulusShowDuration: number;
  recoveryDuration: number;
}
export interface PupillometryData {
  leftPupil: number;
  rightPupil: number;
  stimulusActive?: boolean;
  timestamp: number;
  targetEye?: "Left" | "Right" | "Both";
}

export interface PupillometryTestResult extends BaseTestResult {
  category: TestCategory.Pupillometry;
  results: PupillometryData[];
}
//-------- Ocular Motility Specific Data
/**
 * Configuration options for a custom ocular motility test pattern.
 *
 * @property patternSize - Size of the pattern. For example, if the pattern is a circle, this represents the diameter (not the radius).
 * @property speed - Speed of the target's movement along the pattern. If the pattern is circular, this is the angular speed. This value may be overridden if the movement control mode is set to "ByDuration".
 * @property loopCount - Number of loops the target will complete while following the pattern. This value is ignored if the movement control mode is "ByDuration".
 * @property pauseDuration - Duration of the pause between corners or pattern edges. Useful for collecting more eye data at these points.
 * @property childMode - Smooth pursuit only. Skins the target as a car on a circular road and selects
 * child-friendly virtual assistant videos. Purely cosmetic: path, speed and recorded data are unchanged.
 */

export interface OMTestCustomConfiguration {
  patternSize: number;
  speed: number;
  loopCount: number;
  pauseDuration: number;
  childMode?: boolean;
}

/** Mirrors the headset's SmoothPursuitStageConfig: the orbit, not a pattern. */
export interface SmoothPursuitCustomConfiguration {
  depth: number;
  radiusDegrees: number;
  frequencyHz: number;
  clockwiseDuration: number;
  counterClockwiseDuration: number;
  childMode?: boolean;
}

export interface OcularMotilityData {
  leftGazePosition: PositionWithTimeStamp | null;
  rightGazePosition: PositionWithTimeStamp | null;
  targetPosition: Position | null;
  rightEyeTangentialVelocity: number | null;
  leftEyeTangentialVelocity: number | null;
  rightEyeError: number | null;
  leftEyeError: number | null;
  timestamp: number;
}

export interface OcularMotilityMetrics {
  targetVelocity: number;
  rightMeanError: number;
  rightMeanTangentialVelocity: number;
  rightPursuitGain: number;
  rightSaccadeCount: number;
  leftMeanError: number;
  leftMeanTangentialVelocity: number;
  leftPursuitGain: number;
  leftSaccadeCount: number;
}

export interface OcularMotilityTestResult extends BaseTestResult {
  results: {
    rightGazePositions: Position[];
    leftGazePositions: Position[];
    targetPositions: Position[];
    rightEyeErrors?: number[];
    rightEyeTangentialVelocities?: number[];
    leftEyeErrors?: number[];
    leftEyeTangentialVelocities?: number[];
    timestamps: number[];
    config?: OMTestCustomConfiguration;
    motilityAnalysis?: MotilityAnalysis;
  } & OcularMotilityMetrics;
}
//-------- Visual Acuity Specific Data

export type ScoredLetterSet = {
  value: number;
  letters: string[];
  results?: number[];
  correct?: number;
  _id?: string;
};

export type VAcuityIndex = number;

export type TumblingECollectedData = {
  tumblingESnapshots: TumblingEObject[];
  currentlyActiveTumblingE?: TumblingEObject;
  thresholdRow?: number | null;
  thresholdSnellenSize?: SnellenSize | null;
  chartStimuli?: TumblingEObject[];
};

export type TumblingEObject = {
  _id: string;
  snellenSize: SnellenSize;
  orientation: Direction;
  order: number;
  status: TumblingEStatus;
  row?: number | null;
  column?: number | null;
};

export enum TumblingEStatus {
  CORRECTLY_RESPONDED = "CORRECTLY_RESPONDED",
  INCORRECTLY_RESPONDED = "INCORRECTLY_RESPONDED",
  SKIPPED = "SKIPPED",
  HIDDEN = "HIDDEN",
  CURRENTLY_DISPLAYED = "CURRENTLY_DISPLAYED",
}

export type TumblingEResultData = {
  responses: TumblingEObject[];
  chartStimuli?: TumblingEObject[];
  metrics?: TumblingEMetrics;
  thresholdRow?: number | null;
  thresholdSnellenSize?: SnellenSize | null;
};

export interface VAcuityTestResult extends BaseTestResult {
  category: TestCategory.VisualAcuity;
  values?: ScoredLetterSet[];
  distance?: number;
  finalValue?: number;
  results?: TumblingEResultData;
}

export interface TumblingETestResult extends BaseTestResult {
  category: TestCategory.VisualAcuity;
  results: TumblingEResultData;
}

export interface TumblingEMetrics {
  totalStimuli: number;
  correctResponses: number;
  incorrectResponses: number;
  skippedResponses?: number;
  errorRate: number;
}
// ------- CPT Specific Data

export type CPTDataSnapshot = {
  stimulus?: Stimulus;
  stimulusResponse?: StimulusResponse;
  percentagePoint?: PercentageWithTimeStamp;
  headVelocityData?: HeadVelocity;
  eyeVelocityData?: EyeDisplacement;
  pupilSizeData?: PupilSizeData;
  stageEvent?: ContinuousPerformanceStageEvent;
  isForAdult?: boolean;
};

export interface CPTData {
  stimuli?: Stimulus[];
  responses?: StimulusResponse[];
  headVelocityDatas?: HeadVelocity[];
  headVelocityData?: HeadVelocity[];
  gazePercentageData?: PercentageWithTimeStamp[];
  eyeDisplacements?: DistanceData[];
  pupilSizeData?: PupilSizeData[];
  stageEventList?: ContinuousPerformanceStageEvent[];
  isForAdult?: boolean;
  cptConfigMode?: CPTConfigMode;
  metrics?: CPTScoringResult;
}

export interface CPTRawMetrics {
  nTargets: number;
  nNonTargets: number;
  hits: number;
  timingCount: number;
  misses: number;
  falseAlarms: number;
  correctRejections: number;
  hitRate: number;
  timingRate: number;
  falseAlarmRate: number;
  hitResponseTimesMs: number[];
  meanResponseTimeMs?: number;
  sdResponseTimeMs?: number;
  cvResponseTime?: number;
  medianResponseTimeMs?: number;
  multiPressCount: number;
  multiPressRate: number;
}

/**
 * MOXO performance indices. The four base fields are raw counts, not
 * percentages: `attentiveness` = hits, `timeliness` = timingCount,
 * `impulsiveness` = falseAlarms, `hyperReactivity` = multiPressCount. The
 * dashboard derives everything it renders from `rawMetrics` (see
 * cptStageMetrics/CPTAssessmentUtils).
 *
 * `CPTZScores` keeps each index in its own raw direction — a higher z means
 * more false alarms or more multi-presses, i.e. worse, for `impulsiveness` and
 * `hyperReactivity`. `toPerformanceZ` flips those two so that negative = worse
 * across all four before severity thresholding.
 */
export interface CPTPerformanceScores {
  attentiveness: number;
  timeliness: number;
  impulsiveness: number;
  hyperReactivity: number;
  attentivenessRate: number;
  timelinessRate: number;
  impulsivenessRate: number;
  hyperReactivityRate: number;
}

export interface CPTZScores {
  attentiveness: number;
  timeliness: number;
  impulsiveness: number;
  hyperReactivity: number;
}

export interface CPTScoringResult {
  rawMetrics?: CPTRawMetrics;
  performanceScores: CPTPerformanceScores;
  parametricZScores?: CPTZScores;
  nonParametricZScores?: CPTZScores;
  stages?: CPTStageScoringResult[];
}

export interface CPTStageScoringResult extends Omit<CPTScoringResult, "stages"> {
  stageNumber: number;
  stageName: string;
  stageIndex: number;
  startTime: number;
  endTime: number;
}

export enum StimulusResponseState {
  Correct = "correct",
  Incorrect = "incorrect",
  CorrectMultiPress = "correctMultiPress",
  IncorrectMultiPress = "incorrectMultiPress",
  Miss = "miss",
  FalsePositiveEmpty = "falsePositiveEmpty",
  FalsePositiveAudio = "falsePositiveAudio",
  FalsePositiveVisual = "falsePositiveVisual",
  FalsePositiveAudioVisual = "falsePositiveAudioVisual",
}

export interface StimulusResponse {
  timeStamp: number;
  responseDelta: number;
  state: StimulusResponseState;
}
export interface CPTResult extends BaseTestResult {
  category: TestCategory.ContinuousPerformance;
  results: CPTData;
}

export interface Stimulus {
  id: string;
  timeStamp: number;
  imageName: string;
  type: StimulusType;
  position?: StimulusPosition;
}

export enum StimulusType {
  Target = "target",
  NonTarget = "nonTarget",
  VisualDistractor = "visualDistractor",
  AudioDistractor = "audioDistractor",
  AudioVisualDistractor = "audioVisualDistractor",
}

export enum StimulusPosition {
  Top = "top",
  Left = "left",
  Right = "right",
}
/**
 * Configuration for a stage in the Continuous Performance Test.
 */

export interface CPTCustomConfiguration {
  stages: ContinuousPerformanceStageConfig[];
  isForAdult: boolean;
  isDemoMode: boolean;
  isTriggerClickEnabled: boolean;
  cptConfigMode: CPTConfigMode;
}

export interface ContinuousPerformanceStageConfig {
  /** Display name for this stage */
  stageName: string;

  /** Duration of this stage in seconds */
  stageDuration: number;

  /** How long each stimulus is visible (seconds). For Target / Non Target If this value turns out to be shorter than the time calculated via using targetFrequency, that'll be used instead. */
  stimulusDuration: number;

  /** Faster stimulus duration to be used for special cases (inhibition and vigilance modes) */
  fastStimulusDuration: number;

  /** Display duration of Target / Non Target can be lowered at minimum to this percentage. */
  minAllowedDurationPercentage: number;

  /** Amount of time after displaying a target / non target, that responses are ignored. */
  responseIgnoreDuration: number;

  /** How many targets or non-targets appear per second */
  targetNonTargetFrequency: number;

  /** Faster target non-target frequency to be used for special cases (inhibition and vigilance modes) */
  fastTargetNonTargetFrequency: number;

  /** Probability that a target / non-target stimulus will be a target */
  targetProbability: number;

  /** How many visual distractors appear per second */
  visualDistractorFrequency: number;

  /** How many audio distractors appear per second */
  audioDistractorFrequency: number;

  /** How many audio-visual distractors appear per second */
  audioVisualDistractorFrequency: number;

  /** Number of distractors of the same type to show at the same time */
  simultanousDistractors: number;

  /** Special cases where still random but faster stimuli are displayed. The frequency will be fastTargetNonTargetFrequency */
  fastSequenceCount: number;

  /** Number of consecutive non-targets to show before a target in inhibition sequence */
  inhibitionCount: number;

  /** Number of stimuli to show in each special sequence */
  consecutiveStimulusCount: number;

  /** Number of consecutive targets to show before a non-target in vigilance sequence */
  vigilanceCount: number;
}

export interface ContinuousPerformanceStageEvent extends ContinuousPerformanceStageConfig {
  /** Timestamp when this stage started */
  startTime: number;
  /** Timestamp when this stage ended */
  endTime: number;
}
// ------- Color Vision Specific Data
export interface ColorVisionInitData {
  plateCount: number;
  isOperatorControlled: boolean;
  initialized: boolean;
}

export interface ColorVisionPlateResult {
  plate: number;
  response: string | number;
  isCorrect: boolean;
  expected?: string | number;
  responseTimeMs?: number;
}

export interface ColorVisionCollectedData {
  initData?: ColorVisionInitData;
  latestSnapshot?: ColorVisionPlateResult;
  results?: ColorVisionPlateResult[];
  currentIndex?: number;
}

export interface ColorVisionTestResult extends BaseTestResult {
  category: TestCategory.ColorVision;
  results: boolean[] | ColorVisionPlateResult[];
}

export interface DYCustomConfiguration {
  textOrientation: TextOrientation;
}

export interface DyscalculiaCustomConfiguration {
  dyscalculiaConfigMode: DyscalculiaConfigMode;
}

export interface ReadingAssessmentCustomConfiguration {
  textListType: string;
  testIds: string[];
  showComprehensionQuestions?: boolean;
}

export type DyslexiaGazeSampleV2 = {
  gazePercentageData: [number, number];
  timeStamp: number;
  gazeDelta: number;
  wordIndex: number;
  leftPupilMm: number;
  rightPupilMm: number;
  gazeDirectionVector?: GazeDirectionVector;
  leftGazeDirectionVector?: GazeDirectionVector;
  rightGazeDirectionVector?: GazeDirectionVector;
  gazeOrigin?: GazeDirectionVector;
  headPosition?: GazeDirectionVector;
  headAngularSpeed?: number;
  leftEyeOpenness?: number;
  rightEyeOpenness?: number;
};

export type DyslexiaPageResultV2 = {
  pageIndex: number;
  pageText: string | null;
  startedAt: number;
  samples: DyslexiaGazeSampleV2[];
};

export interface DyslexiaTestResult extends BaseTestResult {
  category: TestCategory.Dyslexia;
  results: {
    gazeDataWithDeltaList: GazeWithDelta[];
    testLanguage: TestLanguage;
  };
}

export type ReadingAssessmentGazeSampleV2 = DyslexiaGazeSampleV2;
export type ReadingAssessmentPageResultV2 = DyslexiaPageResultV2;

export interface ComprehensionAnswer {
  questionIndex: number;
  questionText: string;
  chosenAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  responseTimeMs: number;
}

export interface ReadingAssessmentTestResult extends BaseTestResult {
  category: TestCategory.ReadingAssessment;
  results: {
    schemaVersion: 2;
    textListType: string;
    hash: string;
    pages: ReadingAssessmentPageResultV2[];
    textOrientation: TextOrientation;
    comprehensionAnswers?: ComprehensionAnswer[];
    metrics?: ReadingAssessmentDerivedMetrics;
  };
}

export interface ReadingAssessmentCatalogEntry {
  title?: string;
  textId: string;
  textOrientation?: TextOrientation;
}

export type ReadingFixationTransitionDirection = "forward" | "backward" | "unknown";

export type ReadingFixationDisplayCategory =
  | "forward"
  | "backward"
  | "unknown_transition"
  | "unknown_line"
  | "excessive";

export type ReadingFixationRecord = {
  fixationIndex: number;
  fixationId: number;
  label: string;
  lineIndex: number | null;
  lineId: number | null;
  lineLabel: string;
  transitionDirection: ReadingFixationTransitionDirection;
  displayCategory: ReadingFixationDisplayCategory;
  unknownLineAssignment: boolean;
  excessive: boolean;
  durationSec: number;
  position: PercentagePoint;
  startSampleIndex: number;
  endSampleIndex: number;
  progress01: number | null;
  distanceToLine01: number;
};

export type ReadingAssessmentMetricSummary = {
  totalFixations: number;
  averageFixationDuration: number;
  excessiveFixationsCount: number;
  excessiveFixationsPercentage: number;
  averageExcessiveFixationDuration: number;
  normalFixationsCount: number;
  normalFixationsPercentage: number;
  averageNormalFixationDuration: number;
  wordsPerMinute: number | null;
  readingElapsedSec: number;
  progressedWordCount: number;
  totalWordCount: number;
};

export type ReadingAssessmentPageMetrics = {
  pageIndex: number;
  fixationRecords: ReadingFixationRecord[];
  readingElapsedSec: number;
  progressedWordCount: number;
  totalWordCount: number;
  wordsPerMinute: number | null;
};

export type ReadingAssessmentDerivedMetrics = {
  summary: ReadingAssessmentMetricSummary;
  pageMetrics: ReadingAssessmentPageMetrics[];
};

// ------- Stereoacuity Specific Data

export type StereoAcuityData = {
  isTrue: boolean;
  currentAngleArcsec: number;
  responseTime: number;
};

export type StereoAcuityResultObject = {
  responses: StereoAcuityData[];
  score: number;
  csdScore: number;
};

export interface StereoAcuityTestResult extends BaseTestResult {
  category: TestCategory.StereoAcuity;
  results: StereoAcuityResultObject;
}
// ------- Cover Specific Data

export type CoverTestData = {
  [EyeCoverAction.CoverRightEyeLeftKeptOpen]?: CoverTestDataSnapshot[];
  [EyeCoverAction.OpenRightEyeLeftKeptOpen]?: CoverTestDataSnapshot[];
  [EyeCoverAction.CoverLeftEyeRightKeptOpen]?: CoverTestDataSnapshot[];
  [EyeCoverAction.OpenLeftEyeRightKeptOpen]?: CoverTestDataSnapshot[];
  leftDistanceData?: DistanceData[];
  rightDistanceData?: DistanceData[];
  referenceDatas?: CoverTestReferenceData[];
  objectivePhoria?: ObjectivePhoriaResult;
};

export type CoverTestDataSnapshot = {
  timestamp: number;
  leftGazePosition: Position;
  rightGazePosition: Position;
  eyeCoverAction: EyeCoverAction;
  leftGazeAngles: Position;
  rightGazeAngles: Position;
  conditionName?: string;
  cycleIndex?: number;
  phaseIndex?: number;
};

export interface CoverTestResult extends BaseTestResult {
  category: TestCategory.Cover;
  results: CoverTestData;
}

export interface VideonystagmographyTestResult extends BaseTestResult {
  category: TestCategory.Videonystagmography;
  results: VideonystagmographResultData;
}
// ------- Kinetic Perimetry Specific Data

export interface KineticPerimetryTestResult extends BaseTestResult {
  category: TestCategory.Kinetic;
  results: KineticPerimetryResultData;
}

export interface KineticPerimetryStimulus {
  timestamp: number;
  fixationPointIndex: number;
  stimulusIndex: number;
  distanceRatio: number;
  isResponse: boolean;
}
export interface KineticPerimetryCollectedData {
  responses: KineticPerimetryStimulus[];
  lastStimulus?: KineticPerimetryStimulus;
  kineticGraphStat?: KineticGraphStat;
}

export interface KineticGraphStat {
  maxAngle: number;
  angleIncrement: number;
  eye: EyeType;
}

export interface KineticPerimetryResultData {
  responses: KineticPerimetryStimulus[];
  kineticGraphStat: KineticGraphStat;
}

export enum KineticPerimetryPattern {
  FF_60_12 = "FF_60_12",
  FF_50_12 = "FF_50_12",
  FF_30_8 = "FF_30_8",
  FF_CUSTOM = "FF_CUSTOM",
}

export interface CoverTestReferenceData {
  timestamp: number;
  eyeCoverAction: EyeCoverAction;
  conditionName?: string;
  cycleIndex?: number;
  phaseIndex?: number;
}

export interface ObjectivePhoriaOcclusion {
  conditionName: string;
  cycleIndex: number;
  baselineAction: string;
  occlusionAction: string;
  horizontalDeltaDeg: number;
  horizontalDeltaPd: number;
  verticalDeltaDeg: number;
  verticalDeltaPd: number;
}

export interface ObjectivePhoriaConditionSummary {
  conditionName: string;
  validOcclusionCount: number;
  horizontalPhoriaPd?: number | null;
  horizontalSdPd?: number | null;
  horizontalIqrPd?: number | null;
  verticalPhoriaPd?: number | null;
  verticalSdPd?: number | null;
  verticalIqrPd?: number | null;
  qualityFlag: string;
}

export interface ObjectivePhoriaResult {
  protocol: string;
  phaseDurationSeconds: number;
  cyclesPerCondition: number;
  endpointWindowSeconds: number;
  conditionSummaries: ObjectivePhoriaConditionSummary[];
  occlusions: ObjectivePhoriaOcclusion[];
  overallQualityFlag: string;
}

export interface VideonystagmographyData {
  eyeImageDatas: EyeImageData[];
}

export interface VideonystagmographResultData {
  pupilSizeDatas: PupilSizeData[];
  gazeDatas: GazeData[];
}

export type MagnitudeComparisonResult = {
  isSymbolic?: boolean;
  leftQuantity?: number | string;
  rightQuantity?: number | string;
  choseLeft?: boolean;
  wasCorrect?: boolean;
  reactionTimeSeconds?: number;
};

export type StroopResult = {
  leftNumber?: number;
  rightNumber?: number;
  leftSize?: number;
  rightSize?: number;
  isCongruent?: boolean;
  choseLeft?: boolean;
  wasCorrect?: boolean;
  reactionTimeSeconds?: number;
};

export type DotEnumerationResult = {
  targetCount?: number;
  options?: number[];
  selectedValue?: number;
  wasCorrect?: boolean;
  displayDurationSeconds?: number;
  reactionTimeSeconds?: number;
};

export type DyscalculiaTaskType =
  | "magnitudeComparisonSymbolic"
  | "stroop"
  | "magnitudeComparisonNonSymbolic"
  | "dotEnumeration"
  | "numberLine";

export type DyscalculiaResultItem = MagnitudeComparisonResult | StroopResult | DotEnumerationResult;

export type DyscalculiaSnapshot = {
  taskType: DyscalculiaTaskType;
  taskId?: string;
  result: DyscalculiaResultItem;
};

export type DyscalculiaTaskResult = {
  taskType: DyscalculiaTaskType;
  taskId?: string;
  configMode?: "child" | "adult" | "adultAdaptive" | "adultAdaptiveFast";
  results: DyscalculiaResultItem[];
};

export type DyscalculiaDifficultyLevel = 1 | 2 | 3 | 4;

export type DyscalculiaRiskClassification = "lowRisk" | "atRisk" | "highRisk";

export type DyscalculiaInconclusiveReason =
  | "insufficientItems"
  | "insufficientDifficultyCoverage"
  | "lowConfidence";

export type DyscalculiaLevelAccuracy = {
  level: DyscalculiaDifficultyLevel;
  itemsAnswered: number;
  itemsCorrect: number;
  accuracy: number;
};

export type DyscalculiaLevelSourceCounts = {
  testDesign: number;
  stimulusRule: number;
  unresolved: number;
};

export type DyscalculiaScore = {
  itemsAnswered: number;
  itemsWithResolvedLevel: number;
  levelSourceCounts: DyscalculiaLevelSourceCounts;
  levelAccuracyProfile: DyscalculiaLevelAccuracy[];
  abilityEstimate: number | null;
  classification: DyscalculiaRiskClassification | null;
  classificationConfidence: number | null;
  isClassificationConclusive: boolean;
  inconclusiveReason: DyscalculiaInconclusiveReason | null;
};

export type DyscalculiaTaskScore = DyscalculiaScore & {
  taskType: DyscalculiaTaskType;
  taskId?: string;
  configMode?: string;
  trialLevels: (DyscalculiaDifficultyLevel | null)[];
};

export type DyscalculiaMetrics = DyscalculiaScore & {
  scoringMethod: "difficultyWeightedBayesian";
  tasks: DyscalculiaTaskScore[];
};

export type DyscalculiaTestResult = {
  taskResults?: DyscalculiaTaskResult[];
  metrics?: DyscalculiaMetrics;
};

export interface WCSTCustomConfiguration {
  trialCount?: number;
  ruleChangeWindow?: number;
}

export interface AntiSaccadesCustomConfiguration {
  trialCount: number;
  fixationDurationMinSeconds: number;
  fixationDurationMaxSeconds: number;
  interTrialMinSeconds: number;
  interTrialMaxSeconds: number;
  peripheralVisibleBeforeGuideSeconds: number;
  guidingMoveToCenterDurationSeconds: number;
  minHorizontalDegrees: number;
  maxHorizontalDegrees: number;
  screenDistanceMeters: number;
}

export interface ColorVisionPlate {
  plate: number;
  correct: number | string;
  options: (number | string)[];
  description: string;
}

export interface ColorVisionCustomConfiguration {
  plateCount: ColorVisionPlateNumber;
  questionsJson: ColorVisionPlate[];
  showTutorial?: boolean;
}

export interface VisualAcuityCustomConfiguration {
  showTutorial?: boolean;
}

export interface WCSTCardInfo {
  color?: string;
  shape?: number;
  count?: number;
}

export interface WCSTTrial {
  trialIndex?: number;
  correct: boolean;
  rule?: string;
  reactionTimeMs?: number;
  selectionTime?: number;
  selectedReferenceCard?: WCSTCardInfo;
  playerCard?: WCSTCardInfo;
  controllerUsed?: string;
}

export interface WCSTCollectedData {
  trials: WCSTTrial[];
  averageReactionTimeMs?: number | null;
  averageTrialsToFindRule?: number | null;
}

export interface WCSTSnapshot {
  trial?: WCSTTrial;
  averageReactionTimeMs?: number;
  averageTrialsToFindRule?: number;
}

export interface WCSTTestResult {
  wcstData?: WCSTCollectedData;
  results?: WCSTTrial[];
}
export enum ColorVisionPlateNumber {
  SmallPlateCount = 17,
  MediumPlateCount = 26,
  LargePlateCount = 38,
}

export interface NPCTGazeAngles {
  x: number;
  y: number;
}

export interface NPCTVector3 {
  x: number;
  y: number;
  z: number;
}

export interface NPCTSnapshot {
  timestamp: number;
  distance: number;
  leftPupilMm: number;
  rightPupilMm: number;
  gazeDirectionVector: NPCTVector3;
  leftGazeDirectionVector: NPCTVector3;
  rightGazeDirectionVector: NPCTVector3;
  gazeOrigin: NPCTVector3;
  leftGazeOriginVector: NPCTVector3 | null;
  rightGazeOriginVector: NPCTVector3 | null;
  headPosition: NPCTVector3;
  headAngularSpeed: number;
  leftEyeOpenness: number;
  rightEyeOpenness: number;
  leftGazeAngles: NPCTGazeAngles | null;
  rightGazeAngles: NPCTGazeAngles | null;
  headVelocity: number;
  headAcceleration: number;
  combinedGazeValid: boolean;
  leftGazeValid: boolean;
  rightGazeValid: boolean;
  leftPupilDiameterValid: boolean;
  rightPupilDiameterValid: boolean;
  leftHorizGazeAngleDeg?: number;
  rightHorizGazeAngleDeg?: number;
  expectedConvergenceAngleDeg?: number;
}

export interface NPCTUserInput {
  timestamp: number;
  distance: number;
  isApproaching: boolean;
  isValid: boolean;
}

export interface NPCTTestSettings {
  targetSize: number;
  ipd?: number;
}

export interface NPCTTestResult extends BaseTestResult {
  category: TestCategory.BinocularVision;
  results: {
    testSettings: NPCTTestSettings;
    userInputs: NPCTUserInput[];
    snapshots: NPCTSnapshot[];
  };
}

export type OKNAxis = "Horizontal" | "Vertical";
export type OKNPhase = "Motion" | "Rest";
export type OKNDirection = "LeftToRight" | "RightToLeft" | "BottomToTop" | "TopToBottom";

export interface OKNVector3 {
  x: number;
  y: number;
  z: number;
}

export interface OKNSegment {
  Index?: number;
  Phase?: OKNPhase | string;
  Axis?: OKNAxis | string;
  Direction?: OKNDirection | string;
  SpeedDegPerSecond?: number;
  DurationSeconds?: number;
  StartTimeSeconds?: number;
}

export interface OKNRawSample {
  timestamp: number;
  segmentIndex: number;
  phase: OKNPhase | string;
  axis: OKNAxis | string;
  direction: OKNDirection | string;
  speedDegPerSecond: number;
  elapsedSegmentTime: number;
  leftGazeVector?: OKNVector3 | null;
  rightGazeVector?: OKNVector3 | null;
  combinedGazeVector?: OKNVector3 | null;
  leftPupilSize?: number | null;
  rightPupilSize?: number | null;
  isEyeTrackingValid: boolean;
}

export interface OKNFieldConfig {
  speedsDegPerSecond?: number[];
  motionDurationSeconds?: number;
  restDurationSeconds?: number;
  fieldCoverage?: number;
  fieldHorizontalDegrees?: number;
  fieldVerticalDegrees?: number;
  dotCount?: number;
  dotSizeDegrees?: number;
  dotLuminance?: number;
  stimulusDistanceMeters?: number;
}

export interface OKNProgressSnapshot {
  progressPercent?: number;
  activeSegment?: OKNSegment | null;
  latestSample?: OKNRawSample | null;
}

export interface OKNCollectedData {
  snapshots: OKNProgressSnapshot[];
  rawSamples: OKNRawSample[];
  latestSnapshot?: OKNProgressSnapshot | null;
}

export type OKNEyeKey = "left" | "right";

export interface OKNSlowPhase {
  index: number;
  tMid: number;
  tStart: number;
  tEnd: number;
  spv: number;
  gain: number;
  nSamples: number;
  fitResidualRms: number;
  accepted: boolean;
  rejectReason: string | null;
}

export interface OKNSeriesSummary {
  gainMedian: number | null;
  gainMad: number | null;
  gainMean: number | null;
  gainStd: number | null;
  cumulativeSpvGain: number | null;
  nAccepted: number;
  nRejected: number;
}

export interface OKNSeriesQuality {
  samplingRateHz: number | null;
  pctSamplesLostToBlink: number;
  pctValid: number;
  scorable: boolean;
  flags: string[];
}

export interface OKNSeriesEntry {
  eye: OKNEyeKey;
  axis: string;
  direction: string;
  speedDps: number;
  segmentIndex: number;
  targetVelocityDps: number;
  perSlowPhase: OKNSlowPhase[];
  summary: OKNSeriesSummary;
  quality: OKNSeriesQuality;
}

export interface OKNGainRollups {
  perEyeGain: { left: Record<string, number | null>; right: Record<string, number | null> };
  directionalAsymmetry: Record<string, number | null>;
  interocularAsymmetry: Record<string, number | null>;
}

export interface OKNGainResult {
  series: OKNSeriesEntry[];
  rollups: OKNGainRollups;
  quality: { samplingRateHz: number | null; scorable: boolean };
  config: unknown;
}

export interface OKNTestResult extends BaseTestResult {
  category: TestCategory.OptokineticNystagmus;
  testType: TestType.OptokineticNystagmus;
  results: {
    seed?: number | string | null;
    finished?: boolean;
    fieldConfig?: OKNFieldConfig;
    sequence?: OKNSegment[];
    rawSamples?: OKNRawSample[];
    oknGain?: OKNGainResult;
  };
}

// ------- Anti-Saccades Result Types

export interface AntiSaccadeTrial {
  trialIndex: number;
  peripheralSide: "left" | "right";
  peripheralHorizontalDegrees?: number;
  mirrorHorizontalDegrees?: number;
  plannedFixationDurationSeconds?: number;
  actualFixationDurationSeconds?: number;
  fixationDurationSeconds?: number;
  interTrialDurationSeconds?: number;
  peripheralVisibleBeforeGuideSeconds?: number;
  guidingMoveToCenterDurationSeconds?: number;
  fixationStartedAt?: number;
  peripheralShownAt?: number;
  guidingShownAt?: number;
  completedAt?: number;
  correct: boolean;
  latencyMs?: number;
  reactionTimeMs?: number;
  firstSaccadeAngleDeg?: number;
  antisaccadeAngleDeg?: number;
  peripheralAngleDeg?: number;
}

export interface AntiSaccadesTestResult extends BaseTestResult {
  category: TestCategory.OcularMotility;
  testType: TestType.AntiSaccades;
  results: {
    trials: AntiSaccadeTrial[];
    testSettings?: Partial<AntiSaccadesCustomConfiguration>;
    samples?: AntiSaccadeSample[];
    errorRate?: number;
    meanLatencyMs?: number;
    leftMeanLatencyMs?: number;
    rightMeanLatencyMs?: number;
  };
}

// ------- Anti-Saccades Specific Data

export interface AntiSaccadeVec2 {
  x: number;
  y: number;
}

export interface AntiSaccadeVec3 {
  x: number;
  y: number;
  z: number;
}

export interface AntiSaccadeSample {
  timeStamp: number;
  trialIndex: number;
  trialPhase: string;
  peripheralSide: "left" | "right";
  isPeripheralTargetVisible: boolean;
  isGuidingTargetVisible?: boolean;
  peripheralHorizontalDegrees: number;
  mirrorHorizontalDegrees: number;
  guidingTargetPosition: AntiSaccadeVec3;
  peripheralTargetPosition: AntiSaccadeVec3 | null;
  leftGazeAngles: AntiSaccadeVec2;
  rightGazeAngles: AntiSaccadeVec2;
  combinedGazeValid?: boolean;
  leftGazeValid?: boolean;
  rightGazeValid?: boolean;
}

export interface VFAMetrics {
  md: number;
  psd: number;
  vfi: number;
  ght: string | null;
  meanSensitivity: number;
  reliabilityFactor: number | null;
}

export interface VFABebiePoint {
  rank: number;
  defect: number;
}

export interface VFAMaps {
  thresholds: number[];
  ntd: number[];
  npd: number[];
  ntdGs: number[];
  npdGs: number[];
  ntdPs: number[];
  npdPs: number[];
  angles: number[][];
  bebie: VFABebiePoint[];
}

export interface VFAComputedMetrics {
  metrics: VFAMetrics;
  maps: VFAMaps;
}

/**
 * `VFAComputedMetrics` collapsed into a single lookup. Result views read metrics
 * and maps side by side, so they flatten the contract on the way in rather than
 * threading the nesting through every consumer.
 */
export type VFAFlatMetrics = VFAMetrics & VFAMaps;

/** Storage keys: `diagnosis` is labelled "Interpretation" in translation.json. */
export enum ClinicianLabelField {
  Diagnosis = "diagnosis",
  Reliability = "reliability",
  TestReason = "testReason",
}

export enum ClinicianDiagnosis {
  Normal = "NORMAL",
  Glaucoma = "GLAUCOMA",
  OcularHypertension = "OCULAR_HYPERTENSION",
  Cataract = "CATARACT",
  DiabeticRetinopathy = "DIABETIC_RETINOPATHY",
  MacularDegeneration = "MACULAR_DEGENERATION",
  RetinitisPigmentosa = "RETINITIS_PIGMENTOSA",
  OpticNeuropathy = "OPTIC_NEUROPATHY",
  RetinalDetachment = "RETINAL_DETACHMENT",
  NeurologicalPathway = "NEUROLOGICAL_PATHWAY",
  Amblyopia = "AMBLYOPIA",
  MediaOpacity = "MEDIA_OPACITY",
  Other = "OTHER",
}

export enum ClinicianSeverity {
  Suspect = "SUSPECT",
  Mild = "MILD",
  Moderate = "MODERATE",
  Severe = "SEVERE",
  Early = "EARLY",
  Intermediate = "INTERMEDIATE",
  Advanced = "ADVANCED",
  MildNpdr = "MILD_NPDR",
  ModerateNpdr = "MODERATE_NPDR",
  SevereNpdr = "SEVERE_NPDR",
  Pdr = "PDR",
  LateAtrophic = "LATE_ATROPHIC",
  LateNeovascular = "LATE_NEOVASCULAR",
}

export enum ClinicianReliability {
  Reliable = "RELIABLE",
  Borderline = "BORDERLINE",
  Unreliable = "UNRELIABLE",
  Other = "OTHER",
}

export enum ClinicianTestReason {
  Screening = "SCREENING",
  RoutineFollowUp = "ROUTINE_FOLLOW_UP",
  GlaucomaSuspect = "GLAUCOMA_SUSPECT",
  DiagnosisConfirmation = "DIAGNOSIS_CONFIRMATION",
  VisualSymptoms = "VISUAL_SYMPTOMS",
  PreOperative = "PRE_OPERATIVE",
  PostOperative = "POST_OPERATIVE",
  MedicationMonitoring = "MEDICATION_MONITORING",
  NeurologicalReferral = "NEUROLOGICAL_REFERRAL",
  DrivingAssessment = "DRIVING_ASSESSMENT",
  Research = "RESEARCH",
  Other = "OTHER",
}

export type ClinicianLabelValue = ClinicianDiagnosis | ClinicianReliability | ClinicianTestReason;

export interface ClinicianLabel {
  value: ClinicianLabelValue;
  severity?: ClinicianSeverity;
  freeText?: string;
  userId: string;
  userName?: string;
  changedAt: string;
}

export interface ClinicianLabelHistoryEntry extends ClinicianLabel {
  field: ClinicianLabelField;
  previousValue?: ClinicianLabelValue;
  previousSeverity?: ClinicianSeverity;
}

export interface ClinicianLabels {
  diagnosis?: ClinicianLabel;
  reliability?: ClinicianLabel;
  testReason?: ClinicianLabel;
  history?: ClinicianLabelHistoryEntry[];
}

export interface TestCommentEntry {
  text: string;
  userId: string;
  userName?: string;
  createdAt: string;
}

export interface ClinicianLabelInput {
  value: ClinicianLabelValue;
  severity?: ClinicianSeverity;
  freeText?: string;
}

export interface ClinicianReviewPayload {
  diagnosis?: ClinicianLabelInput | null;
  reliability?: ClinicianLabelInput | null;
  testReason?: ClinicianLabelInput | null;
  comment?: string;
}

export interface VFATestResult extends BaseTestResult {
  category: TestCategory.VisualField;
  points: number[];
  testBehaviour: VFABehaviour;
  fovea?: number;
  blindSpotSeen?: number;
  blindSpotTotal?: number;
  bsAngles?: Record<string, number>;
  fpFnInfo?: { fp: { val: number; total: number }; fn: { val: number; total: number } };
  gaze?: number[];
  displacementList?: EyeDisplacement[];
  computedMetrics?: VFAComputedMetrics;
  clinicianLabels?: ClinicianLabels;
  commentHistory?: TestCommentEntry[];
}
